import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableToken,
  RichTextContent,
  useContentToken,
  useSecondaryColor,
  useTokens,
} from '@vibesites/runtime/react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Relative luminance (0 = black, 1 = white) for a CSS hex / rgb() color.
// Returns null when the color can't be parsed.
function relativeLuminance(color: string | null | undefined): number | null {
  if (!color) return null;
  const c = color.trim().toLowerCase();
  let r: number, g: number, b: number;

  const hexMatch = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split('').map((ch) => ch + ch).join('');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    const rgbMatch = c.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!rgbMatch) return null;
    r = parseInt(rgbMatch[1], 10);
    g = parseInt(rgbMatch[2], 10);
    b = parseInt(rgbMatch[3], 10);
  }

  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function isDarkColor(color: string | null | undefined): boolean {
  const lum = relativeLuminance(color);
  return lum !== null && lum <= 0.5;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ResidentAccessIntro() {
  const eyebrow = useContentToken('residentaccessintro.eyebrow');

  // Eyebrow color: when text-on-secondary reads dark (e.g. #1a1a1a), the
  // secondary surface is light — so a light secondary color (e.g. white) would
  // be invisible on this light section background. Use the dark text-on-secondary
  // color instead. Otherwise the secondary color reads fine and stays on brand.
  const secondaryColor = useSecondaryColor();
  const textOnSecondary = useTokens().color.textOnSecondary;
  const eyebrowColor = isDarkColor(textOnSecondary) ? textOnSecondary : secondaryColor;

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-end">

          {/* Left column: eyebrow + heading */}
          <div>
            <EditableToken
              path="residentaccessintro.eyebrow"
              defaultValue="Make the Most of your Home"
              as="p"
              className="font-body text-sm font-semibold mb-4 tracking-wide"
              style={{ color: eyebrowColor }}
            >
              {eyebrow}
            </EditableToken>

            <RichTextContent
              path="residentaccessintro.heading"
              label="Heading"
              defaultValue="<p>Your Go-To Resource for All Residential Services</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text)] leading-[1.1]"
            />
          </div>

          {/* Right column: body */}
          <RichTextContent
            path="residentaccessintro.body"
            label="Body"
            defaultField="property_applynow_blurb"
            defaultValue='<p>As an <span data-type="field-token" data-field="property_name"></span> resident, you can make payments online, easily submit maintenance requests and contact our team for support anytime &mdash; all in one convenient place.</p>'
            as="div"
            className="resident-access-intro-body font-body text-base text-[var(--vibe-color-text)] leading-relaxed"
          />
        </div>
      </div>

      <style>{`
        .resident-access-intro-body p { margin-bottom: 1rem; }
        .resident-access-intro-body p:last-child { margin-bottom: 0; }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccessIntro',
  displayName: 'Resident Access Intro',
  tokens: {
    'residentaccessintro.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Make the Most of your Home',
    },
    'residentaccessintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Your Go-To Resource for All Residential Services</p>',
    },
    'residentaccessintro.body': {
      label: 'Body',
      type: 'richtext',
      defaultField: 'property_applynow_blurb',
      defaultValue:
        '<p>As an <span data-type="field-token" data-field="property_name"></span> resident, you can make payments online, easily submit maintenance requests and contact our team for support anytime &mdash; all in one convenient place.</p>',
    },
  },
};

(ResidentAccessIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ResidentAccessIntro;
